// Keep the gesture-authorized media element across clips and draw transitions.
// Each caller owns a lease: an old React cleanup cannot stop the next clip.
type Options={volume?:number;playbackRate?:number;kind?:'number'|'countdown'|'announcement';onPlay?:()=>void;onStop?:()=>void;startAtSeconds?:()=>number;finishBeforeNumbers?:boolean};
type Lease={play:()=>Promise<void>;pause:()=>void};
let channel:HTMLAudioElement|undefined,current:Lease|undefined,blocked=false,unlocked=false;
let enabled=false,contextBlocked=false;
let revision=0;
// The opening announcement may finish after the first ball arrives. Keep only
// the latest number, never a backlog; its owner can still cancel it normally.
let finishBeforeNumbers=false,pendingNumber:(()=>void)|undefined;
const listeners=new Set<()=>void>();
const setBlocked=(value:boolean)=>{if(blocked!==value){blocked=value;updateOutput();listeners.forEach(fn=>fn());}};
export const isGameAudioBlocked=()=>enabled&&(blocked||contextBlocked);
export function subscribeGameAudio(listener:()=>void){listeners.add(listener);return()=>{listeners.delete(listener);};}
const silentLease:Lease={async play(){},pause(){}};
const audio=()=>{
 if(!channel){
  channel=new Audio();channel.crossOrigin='anonymous';
  // Speech and music have ONE browser output/permission. An authorized media
  // element must never speak around a suspended music AudioContext.
  gameContext().createMediaElementSource(channel).connect(output!);
 }
 return channel;
};
export function setGameAudioEnabled(value:boolean){
 enabled=value;updateOutput();if(!value)stopGameAudio();audioPermission();
 listeners.forEach(fn=>fn());
}
// Immutable effect files also survive a normal Chromium refresh without a
// media range re-download. The browser releases these small blob URLs on unload.
const cachedSources=new Map<string,Promise<string>>();
export function cachedGameAudioSource(source:string):Promise<string>{
 const existing=cachedSources.get(source);if(existing)return existing;
 const request=fetch(source,{cache:'force-cache'}).then(r=>{if(!r.ok)throw Error('Effect HTTP '+r.status);return r.blob();}).then(blob=>URL.createObjectURL(blob));
 cachedSources.set(source,request);void request.catch(()=>{if(cachedSources.get(source)===request)cachedSources.delete(source);});return request;
}
export function createGameAudio(source:string|readonly string[],options:Options={}):Lease{
 if(!enabled)return silentLease;
 if(options.kind==='number'&&finishBeforeNumbers){
  let next:Lease|undefined,requested=false;
  const resume=()=>{next=createGameAudio(source,options);if(requested)void next.play().catch(()=>{});};
  pendingNumber=resume;
  return {async play(){requested=true;await next?.play();},pause(){if(pendingNumber===resume)pendingNumber=undefined;next?.pause();}};
 }
 ++revision;
 const waiting=options.finishBeforeNumbers?pendingNumber:undefined;
 current?.pause();
 pendingNumber=waiting;
 const sources=typeof source==='string'?[source]:source;let index=0;
 const player=audio();player.src=sources[index];player.volume=options.volume??1;player.playbackRate=options.playbackRate??1;player.loop=false;
 let playRequest=0;
 const owned=()=>current===lease;
 const stop=()=>{if(owned()){finishBeforeNumbers=false;options.onStop?.();const resume=pendingNumber;pendingNumber=undefined;resume?.();}};
 const lease:Lease={
  async play(){
   if(!owned()||!enabled)return;
   const request=++playRequest;
   try{
    const ctx=gameContext();audioPermission();await ctx.resume();audioPermission();
    if(!owned()||!enabled||request!==playRequest)return;
    if(index===0&&options.startAtSeconds)player.currentTime=Math.max(0,options.startAtSeconds());
    await player.play();if(owned()){unlocked=true;setBlocked(false);}
   }
   catch(error){if(owned()){if((error as DOMException).name==='NotAllowedError'){unlocked=false;setBlocked(true);}else stop();}throw error;}
  },
  pause(){
   if(!owned())return;
   player.onplay=null;player.onplaying=null;player.onended=null;player.onpause=null;player.onerror=null;
   current=undefined;pendingNumber=undefined;finishBeforeNumbers=false;player.pause();options.onStop?.();
  }
 };
 current=lease;
 finishBeforeNumbers=!!options.finishBeforeNumbers;
 player.onplay=()=>{if(owned())options.onPlay?.();};
 // Loading/decoding can delay the first audible frame after play(). Re-align
 // a timed effect once when it actually starts; never extend the draw clock.
 player.onplaying=()=>{
  if(!owned()||index!==0||!options.startAtSeconds)return;
  player.onplaying=null;
  const position=Math.max(0,options.startAtSeconds());
  if(Math.abs(player.currentTime-position)>.08)player.currentTime=position;
 };
 player.onended=()=>{
  if(!owned())return;
  if(++index<sources.length){player.src=sources[index];void lease.play().catch(stop);}
  else stop();
 };
 // Natural media completion also emits pause before ended. Only an external
 // interruption ends the lease; ended advances a multi-clip announcement.
 player.onpause=()=>{if(!player.ended)stop();};player.onerror=stop;
 return lease;
}
export function stopGameAudio(){++revision;pendingNumber=undefined;current?.pause();channel?.pause();music?.pause();}
// Probe saved-on on mount, then retry synchronously from a gesture if blocked.
// A stored preference is not a playback grant. Never replay an ended clip or
// fabricate a number/award cue.
export function unlockGameAudio(){
 if(!enabled)return;
 // Resume inside the same user gesture as speech. The music file is loaded
 // only by an enabled music lease, never by this silent permission probe.
 const context=gameContext();void context.resume().catch(()=>{});audioPermission();
 if(current){if(blocked)void current.play().catch(()=>{});return;}
 if(unlocked)return;
 const player=audio(),request=++revision;
 player.loop=false;
 player.src='data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQIAAAAAAA==';
 void player.play().then(()=>{if(request===revision){unlocked=true;setBlocked(false);}}).catch(error=>{if(request===revision&&(error as DOMException).name==='NotAllowedError')setBlocked(true);});
}

// A decoded, cached loop mixes underneath the existing speech element. GainNode
// controls also work on mobile browsers that ignore HTMLMediaElement.volume.
type MusicLease=Lease&{setVolume:(normal:number)=>void};
let context:AudioContext|undefined,output:GainNode|undefined,music:MusicLease|undefined,gain:GainNode|undefined;
let normalVolume=0,musicTarget:number|undefined,bufferSource='',bufferPromise:Promise<AudioBuffer>|undefined;
const audioPermission=()=>{
 const next=enabled&&!!context&&context.state!=='running';
 if(next!==contextBlocked){contextBlocked=next;listeners.forEach(fn=>fn());}
};
function gameContext(){
 if(!context){context=new AudioContext();output=context.createGain();output.connect(context.destination);updateOutput();context.addEventListener('statechange',audioPermission);}
 return context;
}
function updateOutput(){if(output)output.gain.value=enabled&&!blocked?1:0;}
function updateMusicLevel(){
 if(!gain||!context)return;
 // The phase hook owns the configured level. Individual speech clips never
 // introduce another volume rule or silence the background independently.
 const target=normalVolume;
 if(target===musicTarget)return;
 musicTarget=target;
 const now=context.currentTime,value=gain.gain.value;
 gain.gain.cancelScheduledValues(now);gain.gain.setValueAtTime(value,now);
 gain.gain.linearRampToValueAtTime(target,now+(target<value?.08:.5));
}
export function createGameMusic(source:string):MusicLease{
 if(!enabled)return {...silentLease,setVolume(){}};
 music?.pause();const ctx=gameContext(),ownedGain=ctx.createGain();
 ownedGain.gain.value=0;ownedGain.connect(output!);gain=ownedGain;
 normalVolume=0;musicTarget=undefined;
 let node:AudioBufferSourceNode|undefined;
 const owned=()=>music===lease;
 const lease:MusicLease={
  setVolume(normal){if(!owned())return;normalVolume=Math.min(1,Math.max(0,normal));updateMusicLevel();},
  async play(){
   if(!owned()||!enabled)return;
   void ctx.resume().catch(()=>{});audioPermission();
   if(!bufferPromise||bufferSource!==source){
    bufferSource=source;
    const request=fetch(source,{cache:'force-cache'}).then(r=>{if(!r.ok)throw Error('Music HTTP '+r.status);return r.arrayBuffer();}).then(b=>ctx.decodeAudioData(b));
    bufferPromise=request;void request.catch(()=>{if(bufferPromise===request)bufferPromise=undefined;});
   }
   const buffer=await bufferPromise;if(!owned()||!enabled||node)return;
   node=ctx.createBufferSource();node.buffer=buffer;node.loop=true;node.connect(ownedGain);node.start();updateMusicLevel();
  },
  pause(){
   if(!owned())return;
   node?.stop();node?.disconnect();ownedGain.disconnect();music=undefined;gain=undefined;audioPermission();
  }
 };
 music=lease;audioPermission();return lease;
}
